import { computed, toValue, watch, type ComputedRef, type MaybeRefOrGetter } from 'vue';

import { Constants } from '@/constants';
import { getLanguageEnabled } from '@/composables/data-display-utils';
import { useResolvedProjectThemeKey } from '@/composables/useResolvedProjectThemeKey';
import type { LocalizableString, Locales, PageDto, ProjectDto, ThemeHost } from '@/services/types/api.types';
import { useCommunityStore, usePagesStore, useProjectStore } from '@/stores';
import type { BlockParent, BlockParentType } from '@/types/modules';

/** What the block screens show about a block's parent, each kind of parent resolving it its own way. */
interface ResolvedBlockParent<Item> {
  /** The parent, once its store holds it. */
  item: Item | undefined;
  title: LocalizableString | undefined;
  languageEnabled: Locales[];
  themeHost: ThemeHost;
  themeKey: string;
}

/** How the screens of a kind of parent name its blocks and mark one: as the parent's own screen does. */
export interface BlockLabels {
  /** The i18n key of the parent's blocks, as a list's header or a crumb: a project has modules, a page blocks. */
  blocksKey: string;
  /** The i18n key a crumb's list of the parent's blocks shows when there are none. */
  noBlocksKey: string;
  icon: string;
}

const blockLabels: Record<BlockParentType, BlockLabels> = {
  [Constants.Block.ParentType.Project]: {
    blocksKey: 'modules.title',
    noBlocksKey: 'breadcrumbs.no-modules',
    icon: 'o_folder',
  },
  [Constants.Block.ParentType.Page]: {
    blocksKey: 'blocks.title',
    noBlocksKey: 'breadcrumbs.no-blocks',
    icon: 'o_widgets',
  },
};

/** Read without resolving the parent, for a screen that lists a parent's blocks without showing the parent. */
export const blockLabelsOf = (type: BlockParentType): BlockLabels => blockLabels[type];

/** The store a kind of parent is kept in: the parent it holds under an id, and the read that fetches it. */
interface ParentStore<Item> {
  getItem: (id: number) => Item | undefined;
  fetchById: (id: number) => Promise<void>;
}

// A parent is read from its kind's store, and fetched when the store misses it. Its id is undefined while the parent
// is of another kind, and then nothing is read.
function useStoredParent<Item>(id: () => number | undefined, store: ParentStore<Item>): ComputedRef<Item | undefined> {
  watch(
    id,
    async (parentId) => {
      if (parentId !== undefined && !store.getItem(parentId)) await store.fetchById(parentId);
    },
    { immediate: true },
  );

  return computed(() => {
    const parentId = id();
    return parentId === undefined ? undefined : store.getItem(parentId);
  });
}

// A template is a project: `GET /projects/:id` serves it too, so a template missing from the projects store is
// fetched like any project, and drawn with its own theme rather than the community's.
function useProjectParent(id: () => number | undefined): ComputedRef<ResolvedBlockParent<ProjectDto>> {
  const project = useStoredParent(id, useProjectStore());
  const themeKey = useResolvedProjectThemeKey(project);

  return computed(() => ({
    item: project.value,
    title: project.value?.title,
    languageEnabled: project.value ? getLanguageEnabled(project.value) : [],
    themeHost: 'project',
    themeKey: themeKey.value,
  }));
}

// A page is drawn with its own theme, else the community's theme for pages.
function usePageParent(id: () => number | undefined): ComputedRef<ResolvedBlockParent<PageDto>> {
  const communityStore = useCommunityStore();
  const page = useStoredParent(id, usePagesStore());

  return computed(() => ({
    item: page.value,
    title: page.value?.title,
    languageEnabled: page.value ? getLanguageEnabled(page.value) : [],
    themeHost: 'page',
    themeKey: page.value?.theme_key ?? communityStore.community?.default_page_theme_key ?? 'default',
  }));
}

/**
 * Resolves a block's parent to what the block screens show about it, whatever kind of parent it is, so that no screen
 * branches on the kind. The parent is a ref or a getter: Vue reuses a view across routes, so it can change, and what
 * this returns follows it.
 */
export const useBlockParent = (parent: MaybeRefOrGetter<BlockParent>) => {
  // A page and a project can share an id: each kind of parent is handed the id only when the parent is of its kind.
  const idOf = (type: BlockParentType) => () => {
    const { type: parentType, id } = toValue(parent);
    return parentType === type ? id : undefined;
  };
  const projectParent = useProjectParent(idOf(Constants.Block.ParentType.Project));
  const pageParent = usePageParent(idOf(Constants.Block.ParentType.Page));

  const resolved = computed((): ResolvedBlockParent<ProjectDto | PageDto> => {
    const { type } = toValue(parent);
    switch (type) {
      case Constants.Block.ParentType.Project:
        return projectParent.value;
      case Constants.Block.ParentType.Page:
        return pageParent.value;
      default: {
        const unknownType: never = type;
        throw new Error(`Unknown block parent type: ${unknownType}`);
      }
    }
  });

  return {
    item: computed(() => resolved.value.item),
    /** The parent when it is a project, for what only a project has: a Hero can show its project's title. */
    project: computed(() => projectParent.value.item),
    title: computed(() => resolved.value.title),
    languageEnabled: computed(() => resolved.value.languageEnabled),
    themeHost: computed(() => resolved.value.themeHost),
    themeKey: computed(() => resolved.value.themeKey),
    blockLabels: computed(() => blockLabelsOf(toValue(parent).type)),
  };
};
